import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { PopoverPosition, Text } from "@blueprintjs/core";
import { Popover2, Placement } from "@blueprintjs/popover2";
import debounce from "lodash/debounce";
import { MPDIcon } from "src/mpd-library";
import styles from "./styles.module.scss";
import classNames from "classnames";
import { isMobile } from "react-device-detect";
import { Dialog } from "@blueprintjs/core/lib/esm";
import { DropdownSearchHeader } from "../dropdown-search-header/dropdown-search-header";
import { StyledButtonTypes, StyledButton } from "../styled-button";
import { ReactComponent as SearchIcon } from "../../mpd-library/icon/assets/search.svg";
import { ReactComponent as DownArrowIcon } from "../../mpd-library/icon/assets/down-arrow.svg";
import { ReactComponent as LeftArrowBlackIcon } from "../../mpd-library/icon/assets/left-arrow-black.svg";

const classname = "dropdown-select-with-search";

type DropdownSelectWithSearchProps = {
    className?: string;
    title?: string;
    position?: Placement;
    inputPlaceholder: string;
    list: React.ReactElement;
    selectedItemsComponent?: Array<React.ReactElement>;
    contentWrapperClassName?: string;
    isOpenFromParent?: boolean;
    usePortal?: boolean;
    portalClassName?: string;
    disabled?: boolean;
    controlInputFromParent?: boolean;
    searchValueFromParent?: string;
    notShowLeftIcon?: boolean;
    notShowSearchInput?: boolean;
    shouldSetFocusAfterOpenDropdown?: boolean;
    displayInputAndSelectedInOneBlock?: boolean;
    wrapperId: string;
    modifiers?: any;
    onContorolPopoverFromParent?: (status: boolean) => void;
    onChangeSearchInput: (query: string) => void;
    onInputKeyDownBackSpace?: () => void;
};

export const DropdownSelectWithSearch: FC<DropdownSelectWithSearchProps> = ({
    title,
    className,
    position = PopoverPosition.BOTTOM,
    inputPlaceholder = "Search",
    list,
    selectedItemsComponent,
    contentWrapperClassName,
    isOpenFromParent,
    // usePortal = true,
    portalClassName,
    disabled = false,
    controlInputFromParent,
    searchValueFromParent,
    notShowLeftIcon,
    notShowSearchInput = false,
    shouldSetFocusAfterOpenDropdown = false,
    displayInputAndSelectedInOneBlock = false,
    wrapperId,
    modifiers,
    onContorolPopoverFromParent,
    onChangeSearchInput,
    onInputKeyDownBackSpace
}: DropdownSelectWithSearchProps): JSX.Element => {
    const [isOpen, setIsOpen] = useState(false);
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [searchInputValue, setSearchInputValue] = useState("");
    const searchInputRef = useRef(null);
    const debounced = useRef(
        debounce((query) => {
            onChangeSearchInput(query);
        }, 300)
    );

    useEffect(() => {
        debounced.current(controlInputFromParent ? searchValueFromParent : searchInputValue);
    }, [searchInputValue, searchValueFromParent]);

    useEffect(() => {
        if (isOpen && shouldSetFocusAfterOpenDropdown) {
            setIsInputFocused(true);
            searchInputRef.current.focus();
        }
    }, [isOpen, shouldSetFocusAfterOpenDropdown, searchInputRef]);

    useEffect(() => {
        if (!isInputFocused) {
            searchInputRef.current.blur(false);
        }

        if (!isOpen && !isOpenFromParent) {
            searchInputRef.current.blur(false);
        }
    }, [isOpen, isInputFocused]);

    useEffect(() => {
        if (onContorolPopoverFromParent && !isOpenFromParent) {
            setIsOpen(false);
        }
    }, [isOpenFromParent]);

    const onDropdownClick = useCallback(() => {
        if (disabled) {
            return;
        }
        if (onContorolPopoverFromParent) {
            onContorolPopoverFromParent(!isOpen);
        }
        setIsOpen(!isOpen);
    }, [searchInputRef, isOpen]);

    const onInputValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (controlInputFromParent) {
            onChangeSearchInput(event.target.value);
            return;
        }

        setSearchInputValue(event.target.value);
    };

    const onInputBlur = () => {
        setIsInputFocused(false);
    };

    const isOpenCondition = typeof isOpenFromParent === "undefined" ? isOpen : isOpenFromParent;

    const onInputKeyDown = (e) => {
        if (onInputKeyDownBackSpace) {
            const usedSearchString = controlInputFromParent ? searchValueFromParent : searchInputValue;
            if (e.keyCode === 8 && usedSearchString.length === 0) {
                onInputKeyDownBackSpace();
            }
        }
    };

    const onInteraction = (value: boolean) => {
        onContorolPopoverFromParent(value);
        if (!value) {
            setTimeout(() => {
                searchInputRef.current.blur();
                setIsInputFocused(false);
            }, 330);
        }
    };

    if (isMobile) {
        return (
            <>
                <div
                    className={classNames(
                        styles[classname],
                        className,
                        isOpenCondition && styles[`${classname}-opened`],
                        disabled && "mpd-disabled"
                    )}
                    id={"search-dropdown-wrapper"}
                >
                    {title && <Text className={styles[`${classname}-label`]}>{title}</Text>}
                    <div
                        className={classNames(
                            styles[`${classname}-main-block`],
                            displayInputAndSelectedInOneBlock && styles[`${classname}-main-block-dynamic-height`]
                        )}
                        onClick={onDropdownClick}
                    >
                        <div
                            className={classNames(
                                styles[`${classname}-main-block-left`],
                                displayInputAndSelectedInOneBlock && styles[`${classname}-main-block-left-with-wrap`]
                            )}
                        >
                            {!displayInputAndSelectedInOneBlock && (
                                <>
                                    {!notShowLeftIcon && (
                                        <MPDIcon IconComponent={SearchIcon} className={styles["search-icon"]} />
                                    )}
                                    {!!selectedItemsComponent && (
                                        <div
                                            className={classNames(
                                                styles[`${classname}-already-selected-wrapper`],
                                                notShowSearchInput &&
                                                    styles[`${classname}-already-selected-wrapper-wide`],
                                                `${classname}-already-selected-wrapper`
                                            )}
                                        >
                                            {selectedItemsComponent}
                                        </div>
                                    )}
                                    <input
                                        onChange={onInputValueChange}
                                        key={"searchInput"}
                                        type={"text"}
                                        placeholder={inputPlaceholder}
                                        ref={searchInputRef}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onContorolPopoverFromParent(true);
                                        }}
                                        value={controlInputFromParent ? searchValueFromParent : searchInputValue}
                                        // onFocus={onInputFocused}
                                        onBlur={onInputBlur}
                                        disabled
                                        style={{ display: notShowSearchInput ? "none" : "block" }}
                                        className={styles[`${classname}-search-input`]}
                                    />
                                </>
                            )}
                            {displayInputAndSelectedInOneBlock && (
                                <>
                                    {!!selectedItemsComponent && (
                                        <div
                                            className={classNames(
                                                styles[`${classname}-already-selected-and-input-wrapper`],
                                                `${classname}-already-selected-and-input-wrapper`
                                            )}
                                        >
                                            {!notShowLeftIcon && (
                                                <MPDIcon IconComponent={SearchIcon} className={styles["search-icon"]} />
                                            )}
                                            {selectedItemsComponent}
                                            <input
                                                onChange={onInputValueChange}
                                                key={"searchInput"}
                                                type={"text"}
                                                placeholder={selectedItemsComponent.length ? "" : inputPlaceholder}
                                                ref={searchInputRef}
                                                onClick={(e) => e.stopPropagation()}
                                                value={
                                                    controlInputFromParent ? searchValueFromParent : searchInputValue
                                                }
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onContorolPopoverFromParent(true);
                                                }}
                                                // onFocus={onInputFocused}
                                                onBlur={onInputBlur}
                                                disabled={disabled}
                                                style={{ display: notShowSearchInput ? "none" : "block" }}
                                                onKeyDown={onInputKeyDown}
                                            />
                                        </div>
                                    )}
                                </>
                            )}
                            {/* {!notShowSearchInput && ( */}
                            {/* )} */}
                        </div>
                        <MPDIcon IconComponent={DownArrowIcon} className={styles["down-arrow"]} />
                    </div>
                </div>
                <Dialog
                    isOpen={isOpenFromParent === undefined ? optionsOpen : isOpenFromParent}
                    portalClassName={"mpd-select-modal"}
                >
                    <div className={"back-button-wrapper"}>
                        <StyledButton
                            type={StyledButtonTypes.primarySimple}
                            IconComponent={LeftArrowBlackIcon}
                            className={"back-button"}
                            onClick={() => {
                                if (onContorolPopoverFromParent) {
                                    onContorolPopoverFromParent(false);
                                }
                                setIsOpen(false);
                            }}
                        />
                    </div>
                    {/* <input
                        onChange={onInputValueChange}
                        key={"searchInput"}
                        type={"text"}
                        placeholder={inputPlaceholder}
                        ref={searchInputRef}
                        onClick={(e) => {
                            e.stopPropagation();
                            onContorolPopoverFromParent(true);
                        }}
                        value={controlInputFromParent ? searchValueFromParent : searchInputValue}
                        // onFocus={onInputFocused}
                        onBlur={onInputBlur}
                        disabled={disabled}
                        style={{ display: notShowSearchInput ? "none" : "block" }}
                        className={styles[`${classname}-search-input`]}
                    /> */}
                    <DropdownSearchHeader
                        title={`${title?.charAt(0)?.toUpperCase()}${title?.slice(1).toLocaleLowerCase()}`}
                        onSearchChangeHandler={onInputValueChange}
                    />
                    <div className={`${styles[`${classname}-content-wrapper`]} ${contentWrapperClassName}`}>{list}</div>
                </Dialog>
            </>
        );
    }

    return (
        <div className={classNames(styles[`${classname}-main-wrapper`], `${classname}-main-wrapper`)} id={wrapperId}>
            <Popover2
                modifiers={modifiers}
                minimal={false}
                isOpen={isOpenCondition}
                transitionDuration={300}
                enforceFocus={false}
                autoFocus={false}
                portalClassName={portalClassName}
                // popoverClassName={Classes.POPOVER2_CONTENT_SIZING}
                hasBackdrop={!!onContorolPopoverFromParent}
                onInteraction={onInteraction}
                usePortal={false}
                // usePortal
                // lazy
                disabled={disabled}
                // inline={false}
                // backdropClassName={styles[`${classname}-backdrop`]}
                content={
                    <div className={`${styles[`${classname}-content-wrapper`]} ${contentWrapperClassName}`}>{list}</div>
                }
                placement={position}
                // position={'bottom'}
            >
                <div
                    className={classNames(
                        styles[classname],
                        className,
                        isOpenCondition && styles[`${classname}-opened`],
                        disabled && "mpd-disabled"
                    )}
                    id={"search-dropdown-wrapper"}
                >
                    {title && <Text className={styles[`${classname}-label`]}>{title}</Text>}
                    <div
                        className={classNames(
                            styles[`${classname}-main-block`],
                            displayInputAndSelectedInOneBlock && styles[`${classname}-main-block-dynamic-height`]
                        )}
                        onClick={onDropdownClick}
                    >
                        <div
                            className={classNames(
                                styles[`${classname}-main-block-left`],
                                displayInputAndSelectedInOneBlock && styles[`${classname}-main-block-left-with-wrap`]
                            )}
                        >
                            {!displayInputAndSelectedInOneBlock && (
                                <>
                                    {!notShowLeftIcon && (
                                        <MPDIcon IconComponent={SearchIcon} className={styles["search-icon"]} />
                                    )}
                                    {!!selectedItemsComponent && (
                                        <div
                                            className={classNames(
                                                styles[`${classname}-already-selected-wrapper`],
                                                notShowSearchInput &&
                                                    styles[`${classname}-already-selected-wrapper-wide`],
                                                `${classname}-already-selected-wrapper`
                                            )}
                                        >
                                            {selectedItemsComponent}
                                        </div>
                                    )}
                                    <input
                                        onChange={onInputValueChange}
                                        key={"searchInput"}
                                        type={"text"}
                                        placeholder={inputPlaceholder}
                                        ref={searchInputRef}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onContorolPopoverFromParent(true);
                                        }}
                                        value={controlInputFromParent ? searchValueFromParent : searchInputValue}
                                        // onFocus={onInputFocused}
                                        onBlur={onInputBlur}
                                        disabled={disabled}
                                        style={{ display: notShowSearchInput ? "none" : "block" }}
                                        className={styles[`${classname}-search-input`]}
                                    />
                                </>
                            )}
                            {displayInputAndSelectedInOneBlock && (
                                <>
                                    {!!selectedItemsComponent && (
                                        <div
                                            className={classNames(
                                                styles[`${classname}-already-selected-and-input-wrapper`],
                                                `${classname}-already-selected-and-input-wrapper`
                                            )}
                                        >
                                            {!notShowLeftIcon && (
                                                <MPDIcon IconComponent={SearchIcon} className={styles["search-icon"]} />
                                            )}
                                            {selectedItemsComponent}
                                            <input
                                                onChange={onInputValueChange}
                                                key={"searchInput"}
                                                type={"text"}
                                                placeholder={selectedItemsComponent.length ? "" : inputPlaceholder}
                                                ref={searchInputRef}
                                                onClick={(e) => e.stopPropagation()}
                                                value={
                                                    controlInputFromParent ? searchValueFromParent : searchInputValue
                                                }
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onContorolPopoverFromParent(true);
                                                }}
                                                // onFocus={onInputFocused}
                                                onBlur={onInputBlur}
                                                disabled={disabled}
                                                style={{ display: notShowSearchInput ? "none" : "block" }}
                                                onKeyDown={onInputKeyDown}
                                            />
                                        </div>
                                    )}
                                </>
                            )}
                            {/* {!notShowSearchInput && ( */}
                            {/* )} */}
                        </div>
                        <MPDIcon IconComponent={DownArrowIcon} className={styles["down-arrow"]} />
                    </div>
                </div>
            </Popover2>
        </div>
    );
};
